/**
 * Unified Admin Feeds — /app/admin/feeds
 *
 * One UI for all five ingest paths (news / threat-intel / CDM REST / CDM SQL /
 * CDM webhook). Pick a destination, pick a category, drop in a URL or YAML
 * spec, test, save. The router on the backend writes to the right table.
 *
 * Replaces the question "where does this feed go?" with a guided wizard.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Rss, AlertCircle, Database, Cable, Plus, RefreshCw, Trash2, Search,
  CheckCircle, XCircle, Power, FlaskConical, Copy, ExternalLink,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Destination {
  id: 'news' | 'threat-intel' | 'cdm-rest' | 'cdm-sql' | 'cdm-webhook'
  label: string
  description: string
  categories: string[]
  input_kind: string
  icon: string
}

interface Feed {
  id: string
  destination: Destination['id']
  category: string
  label: string
  url: string
  enabled: boolean
  source: 'static' | 'db' | 'cdm'
  last_seen: string | null
  last_24h_count: number | null
  notes?: string
  created_by?: string
}

const DEST_ICON = {
  news: Rss,
  'threat-intel': AlertCircle,
  'cdm-rest': Database,
  'cdm-sql': Database,
  'cdm-webhook': Cable,
} as const

const DEST_COLOR = {
  news: 'text-amber',
  'threat-intel': 'text-rose-light',
  'cdm-rest': 'text-blue',
  'cdm-sql': 'text-violet',
  'cdm-webhook': 'text-emerald',
} as const

const SOURCE_BADGE = {
  static: { label: 'static', className: 'bg-slate/20 text-text-muted' },
  db:     { label: 'db',     className: 'bg-amber/20 text-amber' },
  cdm:    { label: 'cdm',    className: 'bg-blue/20 text-blue' },
} as const

const STARTER_REST_SPEC = `{
  "name": "my-new-source",
  "display_name": "My New Source",
  "archetype": "asset_laptop",
  "kind": "json_api",
  "endpoint": "https://api.example.com/v1",
  "records_path": "/devices",
  "records_root": "$.value",
  "id_path": "$.id",
  "auth": { "kind": "bearer", "bearer_field": "token" },
  "pagination": { "kind": "page_param", "per_page": 100 },
  "field_map": {
    "hostname": "$.name",
    "serial":   "$.serialNumber"
  },
  "match_keys_map": {
    "serial":   "$.serialNumber",
    "hostname": "$.name"
  }
}`

const STARTER_SQL_SPEC = `{
  "name": "my-sql-source",
  "display_name": "My SQL Source",
  "archetype": "asset_server",
  "kind": "sql",
  "driver": "postgresql",
  "limit": 1000,
  "since_param_index": 1,
  "limit_param_index": 2,
  "query": "SELECT id, hostname, serial_number, last_modified FROM cmdb.asset_view WHERE last_modified >= $1 ORDER BY last_modified ASC LIMIT $2",
  "field_map": {
    "hostname": "hostname",
    "serial":   "serial_number"
  },
  "match_keys_map": {
    "serial": "serial_number"
  },
  "id_path": "id",
  "occurred_at_path": "last_modified"
}`

const STARTER_WEBHOOK_CONFIG = `{
  "mapping": {
    "hostname": "$.host",
    "serial":   "$.serial"
  },
  "match_keys_map": {
    "serial": "$.serial"
  },
  "id_path": "$.id",
  "records_root": "$"
}`

export default function AdminFeedsPage() {
  const [destinations, setDestinations] = useState<Destination[]>([])
  const [feeds, setFeeds] = useState<Feed[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<Destination['id'] | 'all'>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Add-new wizard
  const [adding, setAdding] = useState(false)
  const [stepDest, setStepDest] = useState<Destination['id'] | null>(null)
  const [stepCategory, setStepCategory] = useState('')
  const [stepLabel, setStepLabel] = useState('')
  const [stepUrl, setStepUrl] = useState('')
  const [stepConfig, setStepConfig] = useState('')
  const [stepNotes, setStepNotes] = useState('')
  const [testResult, setTestResult] = useState<any>(null)
  const [testing, setTesting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [createdSecret, setCreatedSecret] = useState<{ secret: string, id: string } | null>(null)

  const loadAll = async () => {
    setLoading(true)
    try {
      const [destResp, feedsResp] = await Promise.all([
        api.get('/api/admin/feeds/categories'),
        api.get('/api/admin/feeds'),
      ])
      setDestinations(destResp.data?.destinations || [])
      setFeeds(feedsResp.data?.feeds || [])
    } catch (e) {
      console.error('Failed to load feeds', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadAll() }, [])

  const filtered = useMemo(() => {
    return feeds.filter(f => {
      if (activeFilter !== 'all' && f.destination !== activeFilter) return false
      if (search) {
        const q = search.toLowerCase()
        if (!f.label.toLowerCase().includes(q) && !f.url.toLowerCase().includes(q) && !f.category.toLowerCase().includes(q)) {
          return false
        }
      }
      return true
    })
  }, [feeds, search, activeFilter])

  const grouped = useMemo(() => {
    const m: Record<string, Feed[]> = {}
    for (const f of filtered) {
      const key = f.destination
      if (!m[key]) m[key] = []
      m[key].push(f)
    }
    return m
  }, [filtered])

  const selected = useMemo(
    () => feeds.find(f => f.id === selectedId) || null,
    [feeds, selectedId],
  )

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: feeds.length }
    for (const f of feeds) c[f.destination] = (c[f.destination] || 0) + 1
    return c
  }, [feeds])

  // ── Add-flow handlers ────────────────────────────────────────────────────
  const startAdd = () => {
    setAdding(true)
    setSelectedId(null)
    setStepDest(null)
    setStepCategory('')
    setStepLabel('')
    setStepUrl('')
    setStepConfig('')
    setStepNotes('')
    setTestResult(null)
    setCreatedSecret(null)
  }

  const cancelAdd = () => {
    setAdding(false)
    setCreatedSecret(null)
  }

  const pickDestination = (id: Destination['id']) => {
    setStepDest(id)
    setStepCategory('')
    setStepUrl('')
    setStepConfig(
      id === 'cdm-rest' ? STARTER_REST_SPEC :
      id === 'cdm-sql' ? STARTER_SQL_SPEC :
      id === 'cdm-webhook' ? STARTER_WEBHOOK_CONFIG :
      '',
    )
    setTestResult(null)
  }

  const runTest = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const { data } = await api.post('/api/admin/feeds/test', { url: stepUrl })
      setTestResult(data)
      if (data?.status === 'ok' && !stepLabel) setStepLabel(data.feed_title || '')
    } catch (e: any) {
      setTestResult({ status: 'error', error: e?.message || String(e) })
    } finally {
      setTesting(false)
    }
  }

  const submit = async () => {
    if (!stepDest || !stepCategory || !stepLabel) return
    setSaving(true)
    try {
      const payload: any = {
        destination: stepDest,
        category: stepCategory,
        label: stepLabel,
        notes: stepNotes,
      }
      if (stepDest === 'news' || stepDest === 'threat-intel') {
        payload.url = stepUrl
      } else {
        try {
          payload.config = JSON.parse(stepConfig)
        } catch (e) {
          alert('Config must be valid JSON')
          setSaving(false)
          return
        }
      }
      const { data } = await api.post('/api/admin/feeds', payload)
      if (data?.hmac_secret) {
        setCreatedSecret({ secret: data.hmac_secret, id: data.id })
      } else {
        setAdding(false)
      }
      await loadAll()
    } catch (e: any) {
      alert(`Save failed: ${e?.response?.data?.detail || e?.message || e}`)
    } finally {
      setSaving(false)
    }
  }

  const toggle = async (feed: Feed) => {
    if (feed.source === 'static') return
    try {
      await api.patch(`/api/admin/feeds/${encodeURIComponent(feed.id)}`, { enabled: !feed.enabled })
      await loadAll()
    } catch (e: any) {
      alert(`Toggle failed: ${e?.response?.data?.detail || e?.message}`)
    }
  }

  const remove = async (feed: Feed) => {
    if (feed.source === 'static') return
    if (!confirm(`Delete feed "${feed.label}"?`)) return
    try {
      await api.delete(`/api/admin/feeds/${encodeURIComponent(feed.id)}`)
      if (selectedId === feed.id) setSelectedId(null)
      await loadAll()
    } catch (e: any) {
      alert(`Delete failed: ${e?.response?.data?.detail || e?.message}`)
    }
  }

  const testExisting = async (feed: Feed) => {
    try {
      const { data } = await api.post(`/api/admin/feeds/${encodeURIComponent(feed.id)}/test`)
      alert(JSON.stringify(data, null, 2))
    } catch (e: any) {
      alert(`Test failed: ${e?.response?.data?.detail || e?.message}`)
    }
  }

  const destForSelected = stepDest && destinations.find(d => d.id === stepDest)

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3 animate-fade-in">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Rss size={20} className="text-amber" /> Feeds (Unified Admin)
          </h1>
          <p className="text-[10px] text-text-secondary">
            One screen for all 5 ingest paths · {feeds.length} configured · pick a destination, pick a category, paste a URL.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={startAdd} className="btn-primary text-xs">
            <Plus size={12} /> Add Feed
          </button>
          <button type="button" onClick={loadAll} className="btn-secondary text-xs" disabled={loading}>
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Destination filter chips */}
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => setActiveFilter('all')}
          className={clsx(
            'px-2.5 py-1 rounded text-[10px] font-medium border',
            activeFilter === 'all'
              ? 'bg-blue/20 border-blue/40 text-blue'
              : 'bg-surface border-border text-text-secondary hover:bg-surface-hover',
          )}
        >
          All ({counts.all || 0})
        </button>
        {destinations.map(d => {
          const Icon = DEST_ICON[d.id] || Rss
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => setActiveFilter(d.id)}
              className={clsx(
                'px-2.5 py-1 rounded text-[10px] font-medium border flex items-center gap-1',
                activeFilter === d.id
                  ? 'bg-blue/20 border-blue/40 text-blue'
                  : 'bg-surface border-border text-text-secondary hover:bg-surface-hover',
              )}
            >
              <Icon size={11} className={DEST_COLOR[d.id]} />
              {d.label} ({counts[d.id] || 0})
            </button>
          )
        })}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filter by label, url, or category…"
          className="input pl-7 py-1.5 text-[11px] w-full"
        />
      </div>

      <div className="grid grid-cols-12 gap-3">
        {/* Left rail — feed list */}
        <div className="col-span-12 lg:col-span-5 space-y-2">
          {Object.entries(grouped).length === 0 && (
            <div className="card p-6 text-center text-xs text-text-muted">
              No feeds match the current filter.
            </div>
          )}
          {Object.entries(grouped).map(([dest, list]) => {
            const meta = destinations.find(d => d.id === dest)
            const Icon = DEST_ICON[dest as Destination['id']] || Rss
            return (
              <div key={dest} className="card p-2">
                <div className="flex items-center gap-2 mb-1.5 px-1">
                  <Icon size={12} className={DEST_COLOR[dest as Destination['id']] || ''} />
                  <h3 className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">
                    {meta?.label || dest} ({list.length})
                  </h3>
                </div>
                <div className="space-y-0.5">
                  {list.slice(0, 200).map(f => {
                    const badge = SOURCE_BADGE[f.source] || SOURCE_BADGE.static
                    return (
                      <button
                        type="button"
                        key={f.id}
                        onClick={() => { setSelectedId(f.id); setAdding(false) }}
                        className={clsx(
                          'w-full text-left flex items-center gap-2 px-2 py-1.5 rounded text-[10px] border',
                          selectedId === f.id
                            ? 'bg-blue/10 border-blue/30'
                            : 'bg-transparent border-transparent hover:bg-surface-hover',
                        )}
                      >
                        <span className={clsx(
                          'w-1.5 h-1.5 rounded-full shrink-0',
                          f.enabled ? 'bg-emerald' : 'bg-slate',
                        )} />
                        <span className="text-text-primary truncate flex-1">{f.label}</span>
                        <span className="text-[9px] text-text-muted truncate max-w-[80px] hidden sm:inline">{f.category}</span>
                        <span className={clsx('text-[9px] px-1 rounded', badge.className)}>{badge.label}</span>
                      </button>
                    )
                  })}
                  {list.length > 200 && (
                    <div className="text-[9px] text-text-muted text-center py-1">
                      …and {list.length - 200} more (filter to narrow)
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Centre + right pane */}
        <div className="col-span-12 lg:col-span-7 space-y-3">
          {adding && !createdSecret && (
            <div className="card p-4 border border-blue/20">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-text-primary">Add a new feed</h2>
                <button type="button" onClick={cancelAdd} className="btn-ghost text-[10px]">Cancel</button>
              </div>

              {/* Step 1: destination */}
              <div className="mb-3">
                <div className="text-[10px] font-semibold text-text-muted mb-1.5">Step 1 · Destination</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {destinations.map(d => {
                    const Icon = DEST_ICON[d.id] || Rss
                    return (
                      <button
                        type="button"
                        key={d.id}
                        onClick={() => pickDestination(d.id)}
                        className={clsx(
                          'p-2 rounded border text-left text-[11px] transition',
                          stepDest === d.id
                            ? 'border-blue/40 bg-blue/10'
                            : 'border-border bg-surface hover:bg-surface-hover',
                        )}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon size={13} className={DEST_COLOR[d.id]} />
                          <span className="font-semibold text-text-primary">{d.label}</span>
                        </div>
                        <div className="text-[10px] text-text-muted leading-relaxed">{d.description}</div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {stepDest && destForSelected && (
                <>
                  {/* Step 2: category */}
                  <div className="mb-3">
                    <div className="text-[10px] font-semibold text-text-muted mb-1.5">Step 2 · Category</div>
                    <select
                      value={stepCategory}
                      onChange={e => setStepCategory(e.target.value)}
                      className="input py-1.5 text-[11px] w-full"
                    >
                      <option value="">— pick a category —</option>
                      {destForSelected.categories.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  {/* Step 3: input by destination kind */}
                  <div className="mb-3">
                    <div className="text-[10px] font-semibold text-text-muted mb-1.5">Step 3 · Source</div>
                    {(stepDest === 'news' || stepDest === 'threat-intel') && (
                      <>
                        <input
                          value={stepUrl}
                          onChange={e => setStepUrl(e.target.value)}
                          placeholder="https://example.com/feed.xml"
                          className="input py-2 text-xs w-full mb-2"
                        />
                        <input
                          value={stepLabel}
                          onChange={e => setStepLabel(e.target.value)}
                          placeholder="Display label (e.g. Flying Penguin)"
                          className="input py-2 text-xs w-full"
                        />
                      </>
                    )}
                    {(stepDest === 'cdm-rest' || stepDest === 'cdm-sql' || stepDest === 'cdm-webhook') && (
                      <>
                        <input
                          value={stepLabel}
                          onChange={e => setStepLabel(e.target.value)}
                          placeholder="Display label (e.g. Snipe-IT prod)"
                          className="input py-2 text-xs w-full mb-2"
                        />
                        <textarea
                          value={stepConfig}
                          onChange={e => setStepConfig(e.target.value)}
                          rows={12}
                          spellCheck={false}
                          className="input py-2 text-[10px] font-mono w-full"
                          placeholder="Paste JSON spec…"
                        />
                      </>
                    )}
                  </div>

                  {/* Notes */}
                  <div className="mb-3">
                    <input
                      value={stepNotes}
                      onChange={e => setStepNotes(e.target.value)}
                      placeholder="Notes (optional)"
                      className="input py-1.5 text-[11px] w-full"
                    />
                  </div>

                  {/* Step 4: test */}
                  {(stepDest === 'news' || stepDest === 'threat-intel') && (
                    <div className="mb-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <button
                          type="button"
                          onClick={runTest}
                          disabled={testing || !stepUrl}
                          className="btn-secondary text-[11px]"
                        >
                          {testing ? <RefreshCw size={11} className="animate-spin" /> : <FlaskConical size={11} />}
                          Test feed
                        </button>
                      </div>
                      {testResult && (
                        <div className={clsx(
                          'p-2 rounded text-[10px] mb-2',
                          testResult.status === 'ok'
                            ? 'bg-emerald/10 text-emerald border border-emerald/20'
                            : 'bg-rose/10 text-rose-light border border-rose/20',
                        )}>
                          {testResult.status === 'ok' ? (
                            <>
                              <CheckCircle size={11} className="inline mr-1" />
                              <strong>{testResult.feed_title}</strong> — {testResult.total_entries} entries
                              {testResult.preview?.length > 0 && (
                                <ul className="mt-1 ml-4 list-disc space-y-0.5">
                                  {testResult.preview.map((p: any, i: number) => (
                                    <li key={i} className="truncate">{p.title}</li>
                                  ))}
                                </ul>
                              )}
                            </>
                          ) : (
                            <><XCircle size={11} className="inline mr-1" /> {testResult.error}</>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 5: save */}
                  <div className="flex justify-end gap-2 pt-2 border-t border-border">
                    <button type="button" onClick={cancelAdd} className="btn-ghost text-xs">Cancel</button>
                    <button
                      type="button"
                      onClick={submit}
                      disabled={saving || !stepDest || !stepCategory || !stepLabel}
                      className="btn-primary text-xs"
                    >
                      {saving ? <RefreshCw size={11} className="animate-spin" /> : <Plus size={11} />}
                      Save feed
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {createdSecret && (
            <div className="card p-4 border border-emerald/40 bg-emerald/5">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={16} className="text-emerald" />
                <h2 className="text-sm font-semibold text-text-primary">Webhook secret minted — save it now</h2>
              </div>
              <p className="text-[11px] text-text-secondary mb-2">
                This secret will not be shown again. Use it as the HMAC-SHA256 key over the raw POST body and pass the hex digest as <code>X-Signature: sha256=&lt;hex&gt;</code>.
              </p>
              <div className="flex items-center gap-2 bg-surface p-2 rounded border border-border font-mono text-[11px] break-all">
                <code className="flex-1">{createdSecret.secret}</code>
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(createdSecret.secret)}
                  className="btn-ghost text-[10px]"
                >
                  <Copy size={11} /> Copy
                </button>
              </div>
              <div className="mt-3 flex justify-end">
                <button type="button" onClick={() => { setCreatedSecret(null); setAdding(false) }} className="btn-primary text-xs">
                  Done
                </button>
              </div>
            </div>
          )}

          {!adding && !createdSecret && selected && (
            <div className="card p-4">
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] text-text-muted uppercase tracking-wider">
                    {destinations.find(d => d.id === selected.destination)?.label || selected.destination} · {selected.category}
                  </div>
                  <h2 className="text-base font-semibold text-text-primary truncate">{selected.label}</h2>
                  <a href={selected.url} target="_blank" rel="noreferrer" className="text-[10px] text-blue hover:underline flex items-center gap-1 truncate">
                    {selected.url} <ExternalLink size={9} />
                  </a>
                </div>
                <span className={clsx('text-[10px] px-1.5 py-0.5 rounded shrink-0', SOURCE_BADGE[selected.source]?.className)}>
                  {SOURCE_BADGE[selected.source]?.label || selected.source}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="text-[10px]">
                  <div className="text-text-muted">Status</div>
                  <div className={clsx('font-semibold', selected.enabled ? 'text-emerald' : 'text-text-muted')}>
                    {selected.enabled ? 'Enabled' : 'Disabled'}
                  </div>
                </div>
                <div className="text-[10px]">
                  <div className="text-text-muted">Last seen</div>
                  <div className="font-semibold text-text-primary">
                    {selected.last_seen ? new Date(selected.last_seen).toLocaleString() : '—'}
                  </div>
                </div>
                <div className="text-[10px]">
                  <div className="text-text-muted">Last 24h</div>
                  <div className="font-semibold text-text-primary">
                    {selected.last_24h_count ?? '—'}
                  </div>
                </div>
              </div>

              {selected.notes && (
                <div className="text-[10px] text-text-secondary mb-3 p-2 bg-surface rounded border border-border">
                  {selected.notes}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => testExisting(selected)} className="btn-secondary text-[11px]">
                  <FlaskConical size={11} /> Test
                </button>
                {selected.source !== 'static' && (
                  <>
                    <button type="button" onClick={() => toggle(selected)} className="btn-secondary text-[11px]">
                      <Power size={11} /> {selected.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button type="button" onClick={() => remove(selected)} className="btn-secondary text-[11px] !border-rose/30 !text-rose-light hover:!bg-rose/10">
                      <Trash2 size={11} /> {selected.source === 'cdm' ? 'Disable (soft)' : 'Delete'}
                    </button>
                  </>
                )}
                {selected.source === 'static' && (
                  <span className="text-[10px] text-text-muted py-1">
                    Static feed (mega_feeds.py / threat_feeds.py) — read-only here.
                  </span>
                )}
              </div>
            </div>
          )}

          {!adding && !createdSecret && !selected && (
            <div className="card p-6 text-center text-xs text-text-muted">
              <Rss size={28} className="mx-auto text-text-muted mb-2" />
              <div>Pick a feed on the left, or click <strong>Add Feed</strong> to wire a new source.</div>
              <div className="mt-2 text-[10px]">
                The legacy <a href="/app/feed-manager" className="text-blue hover:underline">/app/feed-manager</a> page still works for Redis-backed news feeds, but this is the recommended entry point going forward.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
